import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ClientStats } from '@/components/clients/ClientStats';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Edit, Mail, Phone, Globe, MapPin, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { mockClients, mockProjects, mockContracts } from '@/lib/mock-entities';

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', id],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return mockClients.find((c) => c.id === id);
    },
  });

  const { data: clientProjects } = useQuery({
    queryKey: ['client-projects', id],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return mockProjects.filter((p) => p.clientId === id).slice(0, 5);
    },
  });

  const { data: clientContracts } = useQuery({
    queryKey: ['client-contracts', id],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return mockContracts.filter((c) => c.clientId === id);
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Client Details" />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Client Not Found" />
        <main className="flex-1 overflow-auto p-6">
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Client not found</p>
            <Button onClick={() => navigate('/clients')}>Back to Clients</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title={client.companyName}>
        <Button onClick={() => navigate(`/clients/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Client
        </Button>
      </Header>

      <main className="flex-1 overflow-auto p-6">
        {/* Stats Section */}
        <div className="mb-6">
          <ClientStats client={client} />
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="contracts">Contracts</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Mail className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{client.email}</p>
                    </div>
                  </div>
                  {client.phone && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Phone className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium">{client.phone}</p>
                      </div>
                    </div>
                  )}
                  {client.website && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Globe className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Website</p>
                        <a
                          href={client.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary-600 hover:underline"
                        >
                          {client.website}
                        </a>
                      </div>
                    </div>
                  )}
                  {client.address && (
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <MapPin className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Address</p>
                        <p className="font-medium">{client.address}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Client Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                    <p className="font-medium">{client.contactPerson}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Status</p>
                    <Badge
                      variant={
                        client.status === 'active'
                          ? 'default'
                          : client.status === 'lead'
                            ? 'outline'
                            : 'secondary'
                      }
                    >
                      {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Source</p>
                    <p className="font-medium capitalize">{client.source}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Created</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <p className="font-medium">
                        {format(new Date(client.createdAt), 'MMMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  {client.notes && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Notes</p>
                      <p className="text-sm">{client.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <CardTitle>Recent Projects</CardTitle>
              </CardHeader>
              <CardContent>
                {!clientProjects || clientProjects.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No projects found</p>
                ) : (
                  <div className="space-y-4">
                    {clientProjects.map((project) => (
                      <div
                        key={project.id}
                        className="p-4 border rounded-lg hover:border-primary-300 cursor-pointer transition-colors"
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium">{project.name}</h4>
                          <Badge
                            variant={
                              project.status === 'completed'
                                ? 'default'
                                : project.status === 'in-progress'
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {project.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{project.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Budget: ${project.budget.toLocaleString()}</span>
                          <span>Progress: {project.progress}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts">
            <Card>
              <CardHeader>
                <CardTitle>Contracts</CardTitle>
              </CardHeader>
              <CardContent>
                {!clientContracts || clientContracts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No contracts found</p>
                ) : (
                  <div className="space-y-4">
                    {clientContracts.map((contract) => (
                      <div
                        key={contract.id}
                        className="p-4 border rounded-lg hover:border-primary-300 cursor-pointer transition-colors"
                        onClick={() => navigate(`/contracts/${contract.id}`)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium">{contract.contractNumber}</h4>
                            {contract.projectName && (
                              <p className="text-sm text-gray-600">{contract.projectName}</p>
                            )}
                          </div>
                          <Badge
                            variant={
                              contract.status === 'active'
                                ? 'default'
                                : contract.status === 'completed'
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {contract.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Value: ${contract.totalValue.toLocaleString()}</span>
                          <span>Paid: ${contract.paidAmount.toLocaleString()}</span>
                          <span className="capitalize">{contract.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 text-center py-8">
                  Activity tracking coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
